import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ChevronLeft, DollarSign, AlertTriangle, TrendingUp, Calendar,
    Sparkles, CheckSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DealDetail() {
    const { dealId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [deal, setDeal] = useState(null);
    const [account, setAccount] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [loadingAI, setLoadingAI] = useState(false);

    useEffect(() => {
        if (dealId) loadDeal();
    }, [dealId]);

    const loadDeal = async () => {
        try {
            const headers = getAuthHeader();
            const res = await axios.get(`${API}/deals/${dealId}`, { headers });
            setDeal(res.data);

            const [accountRes, activitiesRes] = await Promise.all([
                res.data.account_id ? axios.get(`${API}/accounts/${res.data.account_id}`, { headers }).catch(() => null) : null,
                axios.get(`${API}/activities?deal_id=${dealId}`, { headers }).catch(() => ({ data: [] })),
            ]);
            if (accountRes) setAccount(accountRes.data);
            setActivities(activitiesRes?.data || []);
        } catch { toast.error('Failed to load deal'); }
        finally { setLoading(false); }
    };

    const runAIAnalysis = async () => {
        setLoadingAI(true);
        try {
            const res = await axios.post(`${API}/ai/analyze-deal/${dealId}`, {}, { headers: getAuthHeader() });
            setAiAnalysis(res.data);
            setDeal(prev => ({ ...prev, ai_risk_score: res.data.risk_score, ai_insights: res.data.insights }));
            toast.success('AI analysis complete');
        } catch { toast.error('AI analysis failed'); }
        finally { setLoadingAI(false); }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;
    if (!deal) return null;

    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

    return (
        <div className="space-y-6 max-w-5xl animate-fade-in">
            {/* Header */}
            <div className="flex items-start gap-4">
                <button onClick={() => navigate('/pipeline')} className="mt-1 p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600" data-testid="deal-back-btn">
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <div className="flex items-start justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="deal-title">{deal.title}</h1>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-stone-500">
                                    {account?.name || deal.account_name}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                                    deal.stage === 'closed_won' ? 'bg-emerald-50 text-emerald-600' :
                                    deal.stage === 'closed_lost' ? 'bg-red-50 text-red-600' :
                                    'bg-stone-100 text-stone-700'
                                }`}>
                                    {deal.stage?.replace(/_/g, ' ')}
                                </span>
                            </div>
                        </div>
                        <Button onClick={runAIAnalysis} disabled={loadingAI} className="ai-gradient text-white hover:opacity-90" data-testid="run-ai-analysis-btn">
                            <Sparkles className="w-4 h-4 mr-2" />
                            {loadingAI ? 'Analyzing...' : 'Run AI Analysis'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-4 gap-4" data-testid="deal-metrics">
                {[
                    { label: 'Deal Value', value: formatVal(deal.value || 0), icon: DollarSign, color: 'text-emerald-600' },
                    { label: 'Risk Score', value: deal.ai_risk_score != null ? `${deal.ai_risk_score}%` : 'N/A', icon: AlertTriangle, color: (deal.ai_risk_score || 0) >= 70 ? 'text-red-600' : 'text-amber-600' },
                    { label: 'Win Probability', value: deal.probability != null ? `${deal.probability}%` : 'N/A', icon: TrendingUp, color: 'text-blue-600' },
                    { label: 'Close Date', value: deal.expected_close || 'TBD', icon: Calendar, color: 'text-violet-600' },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-4">
                        <div className="flex items-center gap-2 mb-1">
                            <m.icon className={`w-4 h-4 ${m.color}`} />
                            <p className="text-xs text-stone-400 font-medium">{m.label}</p>
                        </div>
                        <p className={`text-lg font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                    </div>
                ))}
            </div>

            {/* Deal Info + AI Insights */}
            <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                    <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Deal Information</h3>
                    <div className="space-y-3">
                        {[
                            { label: 'Company', value: account?.name || deal.account_name, link: account ? `/accounts/${account.account_id}` : null },
                            { label: 'Source', value: deal.source },
                            { label: 'Currency', value: deal.currency },
                        ].map(f => (
                            <div key={f.label} className="flex gap-4">
                                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider w-24 flex-shrink-0 mt-0.5 capitalize">{f.label}</span>
                                {f.link ? (
                                    <button onClick={() => navigate(f.link)} className="text-sm text-stone-700 hover:text-stone-900">{f.value}</button>
                                ) : (
                                    <span className="text-sm text-stone-700 capitalize">{f.value || '—'}</span>
                                )}
                            </div>
                        ))}
                        {deal.description && (
                            <div>
                                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider block mb-1">Description</span>
                                <p className="text-sm text-stone-600">{deal.description}</p>
                            </div>
                        )}
                        {(deal.tags || []).length > 0 && (
                            <div>
                                <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider block mb-1">Tags</span>
                                <div className="flex flex-wrap gap-1">
                                    {deal.tags.map(t => <span key={t} className="text-xs px-2 py-0.5 bg-stone-100 text-stone-700 rounded-full">{t}</span>)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-stone-200 p-6" style={{ borderTop: '3px solid transparent', borderImage: 'linear-gradient(135deg, #6366f1, #a855f7) 1' }}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-6 h-6 ai-gradient rounded-md flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-white" />
                        </div>
                        <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Insights</h3>
                    </div>
                    {deal.ai_insights ? (
                        <div className="text-sm text-stone-700 leading-relaxed whitespace-pre-line">{deal.ai_insights}</div>
                    ) : (
                        <div className="text-center py-4">
                            <Sparkles className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                            <p className="text-sm text-stone-400">Run AI analysis to get deal insights</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Activities */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <h3 className="font-semibold text-stone-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Activities ({activities.length})</h3>
                {activities.length === 0 ? (
                    <div className="text-center py-6 text-stone-400">
                        <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No activities logged for this deal</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {activities.map(a => (
                            <div key={a.activity_id} className="flex items-start gap-3 p-3 rounded-lg bg-stone-50 border border-stone-100">
                                <div className="w-7 h-7 rounded-full bg-white border border-stone-200 flex items-center justify-center text-sm flex-shrink-0">
                                    {a.type === 'email' ? <span>E</span> : a.type === 'call' ? <span>C</span> : a.type === 'meeting' ? <span>M</span> : <span>N</span>}
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-stone-900">{a.title}</p>
                                    <p className="text-xs text-stone-400">{a.description || a.type}</p>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{a.status}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
